import { ChangeEvent, ReactElement, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { Dropdown, Form, NavDropdown, NavItem, NavLink as NavLinkBoot } from 'react-bootstrap';
import { selectDevice } from '../redux/slicers/deviceSlice';
import { selectOrganization, unSelectOrganization } from '../redux/slicers/organizationSlice';
import { selectBuilding, unSelectBuilding } from '../redux/slicers/buildingSlice';
import { fetchTagsThunk } from '../redux/slicers/tagSlice';

const NavDeviceList = (): ReactElement => {
	const dispatch = useAppDispatch();
	const { selectedOrganization, organizations } = useAppSelector((state) => state.organization);
	const { selectedBuilding, buildings } = useAppSelector((state) => state.building);
	const { selectedDevice, devices } = useAppSelector((state) => state.device);

	const currentOrganizations = useMemo(() => Object.values(organizations), [organizations]);

	const currentBuildings = useMemo(() => {
		if (selectedOrganization) {
			return Object.values(buildings).filter(
				(building) => building.organization === selectedOrganization.id
			);
		}
		return Object.values(buildings);
	}, [buildings, selectedOrganization]);

	const currentDevices = useMemo(() => {
		if (selectedOrganization && selectedBuilding) {
			return Object.values(devices).filter(
				(device) =>
					device.organization === selectedOrganization.id && device.building === selectedBuilding.id
			);
		}
		if (selectedBuilding) {
			return Object.values(devices).filter((device) => device.building === selectedBuilding.id);
		}
		if (selectedOrganization) {
			return Object.values(devices).filter(
				(device) => device.organization === selectedOrganization.id
			);
		}
		return Object.values(devices);
	}, [devices, selectedBuilding, selectedOrganization]);

	function changeOrganization(event: ChangeEvent<HTMLSelectElement>) {
		if (selectedBuilding) {
			dispatch(unSelectBuilding());
		}
		if (event.target.value === 'Select organization') {
			dispatch(unSelectOrganization());
		} else if (event.target.value) {
			dispatch(selectOrganization(Number(event.target.value)));
		}
	}

	function changeBuilding(event: ChangeEvent<HTMLSelectElement>) {
		if (event.target.value === 'Select building') {
			dispatch(unSelectBuilding());
		} else if (event.target.value) {
			dispatch(selectBuilding(Number(event.target.value)));
		}
	}

	function changeDevice(id: number) {
		dispatch(selectDevice(id));
		dispatch(
			fetchTagsThunk({
				params: {
					device: String(id),
					fields: 'id,name,active,description,modality_type,device,type',
				},
			})
		);
	}

	return (
		<Dropdown as={NavItem} align="end" aria-label="Device selection link" id="current-device-list">
			<Dropdown.Toggle as={NavLinkBoot} className="text-truncate">
				{selectedDevice ? selectedDevice.name : 'Unknown device'}
			</Dropdown.Toggle>
			<Dropdown.Menu
				className="overflow-y-auto overflow-x-hidden w-auot"
				style={{ maxHeight: '450px', minWidth: '250px' }}
			>
				<Form.Select
					aria-label="Organization select"
					id="current-organization-list"
					className="mx-3 my-2 text-truncate"
					style={{ width: 'calc(100% - 2rem)' }}
					value={selectedOrganization?.id}
					onChange={changeOrganization}
				>
					<option id="organization-select" value={undefined}>
						Select organization
					</option>
					{currentOrganizations.map((organization) => (
						<option
							key={`organization-${organization.id}`}
							id={`organization-${organization.id}`}
							value={organization.id}
						>
							{organization.name}
						</option>
					))}
				</Form.Select>
				<Form.Select
					aria-label="Building select"
					id="current-building-list"
					className="mx-3 my-2 text-truncate"
					style={{ width: 'calc(100% - 2rem)' }}
					value={selectedBuilding?.id}
					onChange={changeBuilding}
				>
					<option id="building-select" value={undefined}>
						Select building
					</option>
					{currentBuildings.map((building) => (
						<option
							key={`building-${building.id}`}
							id={`building-${building.id}`}
							value={building.id}
						>
							{building.name}
						</option>
					))}
				</Form.Select>
				<NavDropdown.Divider className="my-3" />
				{currentDevices.length > 0 ? (
					currentDevices.map((device) => (
						<NavDropdown.Item
							key={`device-${device.id}`}
							id={`device-${device.id}`}
							onClick={() => changeDevice(device.id)}
						>
							{device.name ? device.name : `Unknown device: id-${device.id}`}
						</NavDropdown.Item>
					))
				) : (
					<NavDropdown.ItemText id="missing-devices">Missing devices</NavDropdown.ItemText>
				)}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export default NavDeviceList;
